/**
 * Report Template Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { templateService } from './template.service'
import type {
  CreateTemplateData,
  UpdateTemplateData,
  TemplateFilters,
} from '../../domain/report-template/types'

const QUERY_KEYS = {
  templates: 'report-templates',
  templatesByModality: (code: string) => ['report-templates', 'modality', code],
  templatesByInstitution: (id: string | null) => ['report-templates', 'institution', id],
  template: (id: string) => ['report-template', id],
}

/**
 * Hook for searching templates with filters
 */
export function useTemplates(filters?: TemplateFilters) {
  return useQuery({
    queryKey: [QUERY_KEYS.templates, filters],
    queryFn: () => templateService.search(filters),
  })
}

/**
 * Hook for getting templates by modality (for viewer dropdown)
 */
export function useTemplatesByModality(modalityCode: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.templatesByModality(modalityCode || ''),
    queryFn: () => templateService.getByModality(modalityCode!),
    enabled: !!modalityCode,
    staleTime: 5 * 60 * 1000, // 5 minutes - templates don't change often
  })
}

/**
 * Hook for getting templates by institution
 */
export function useTemplatesByInstitution(institutionId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.templatesByInstitution(institutionId),
    queryFn: () => templateService.getByInstitution(institutionId),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook for getting a single template
 */
export function useTemplate(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.template(id),
    queryFn: () => templateService.getById(id),
    enabled: !!id,
  })
}

/**
 * Hook for creating a template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTemplateData) => templateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.templates] })
    },
  })
}

/**
 * Hook for updating a template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateData }) =>
      templateService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.templates] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.template(variables.id) })
    },
  })
}

/**
 * Hook for deleting a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => templateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.templates] })
    },
  })
}

export { templateService }
